// assetFormShared — the ONE form body behind the asset containers (ADR-038
// made literal — the container judged in the Workbench, architect-ruled):
// the create WIZARD's steps and the two edit drawers register against the same field
// sections, so create · edit · books can never drift apart. Page-local form
// model (the wire schema is `CreateAssetInputSchema`/`EditAssetInputSchema`;
// this shape is the UI's, mapped at submit).

import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';

import {
  ASSET_CATEGORY_DESCRIPTORS,
  factState,
  isBooksCategory,
  isConnectivityNa,
  isNetworkCapable,
  ratingUnitFor,
  type AssetCategory,
  type AssetCategoryCharter,
  type ModelSpecColumn,
} from '@ems-app/schemas/assets/categories';
import { BATTERY_CHEMISTRIES, BATTERY_TECHNOLOGIES, TECHNOLOGY_CHEMISTRY, type BatteryChemistry, type BatteryTechnology } from '@ems-app/schemas/common/battery';
import type { CreateAssetModelRequest } from '@ems-app/schemas/catalog/requests';
import type { AssetModelSearchItem } from '@ems-app/schemas/catalog/responses';
import type { AssetModelRef } from '@ems-app/schemas/assets/responses';
import { Button } from '../ui/Button.js';
import { Picker } from './Picker.js';
import { useProductFace } from './lookupFaces.js';
import { icons } from '../ui/icons.js';
import { minorUnitsOf } from '@ems-app/schemas/common/currency';
import { productSource } from '../../hooks/useAssetModels.js';
import { ownersSource } from '../../hooks/useAssetOwners.js';
import { projectCurrencySource } from '../../hooks/useCurrencies.js';
import { useProject } from '../../hooks/useProject.js';
import { useWeekStart } from '../../hooks/useWeekStart.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { useLookup, type LookupSource } from '../../hooks/useLookup.js';
import { codeSource, parentedCodeSource, type CodeSource, type OptionRow, type ParentedCodeSource, type ServerSource, type SmallSource } from '../../hooks/useOptions.js';
import { DatePicker } from '../ui/DatePicker.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { NumberInput } from '../ui/NumberInput.js';
import { Toggle } from '../ui/Toggle.js';


// ── form model ───────────────────────────────────────────────────────────────

export const AssetFormSchema = z.object({
  category: z.string().min(1),
  modelKey: z.string().nullable(), // a product's id (the category's generic row is one) | null
  localMode: z.boolean(),
  localModelName: z.string().trim().max(96),
  localModelMaker: z.string().trim().max(64),
  // Identity facts of the quick birth (the skinny door — architect-ruled
  // 2026-08-28: name · maker · series · part number · the category's specs ·
  // the networked switch; the capability/interfaces declaration lives on the
  // catalog surface's own stepped create/edit, never inline).
  localSeries: z.string().trim().max(48),
  localPartNumber: z.string().trim().max(64),
  localSpecPowerKw: z.number().nullable(),
  localSpecCapacityKva: z.number().nullable(),
  localSpecCapacityAh: z.number().nullable(),
  localSpecVoltageV: z.number().nullable(),
  localSpecWattPeakW: z.number().nullable(),
  localSpecCoolingKw: z.number().nullable(),
  localSpecTankL: z.number().nullable(),
  localSpecBatteryChem: z.string().nullable(),
  localSpecBatteryTech: z.string().nullable(),
  localWarrantyMonths: z.number().nullable(),
  // The one device fact of the quick birth: the networked switch (pre-set from
  // the category's connectivity default when local mode opens). Capabilities +
  // interfaces are the catalog surface's deliberate door (ProductCreateWizard).
  localNetworked: z.boolean(),
  ratingValue: z.number().nullable(),
  ratingUnit: z.string().nullable(),
  reference: z.string().trim().min(1).max(48),
  referenceAlt: z.string().trim().max(48),
  serial: z.string().trim().max(64),
  name: z.string().trim().max(64),
  unitCount: z.number().nullable(),
  condition: z.enum(['good', 'fair', 'poor', 'failed']).nullable(),
  soh: z.number().nullable(),
  // location (wizard only — the drawers never carry it; moves are verbs)
  locationKind: z.enum(['site', 'depot']),
  siteId: z.string().nullable(),
  status: z.enum(['deployed', 'stocked']),
  sinceOn: z.string().nullable(),
  parentId: z.string().nullable(),
  // acquisition (the books facet; the record drawer carries only the pair).
  // 'other' exists so a hydrated row can never break its own edit form; the
  // currency is the project's by default (pre-filled when the project is
  // known), any code the runtime lists may be chosen — an amount keeps the
  // currency it was entered in (common/currency).
  booksOwner: z.string().nullable(),
  booksMode: z.enum(['purchased', 'leased', 'customer_provided', 'other']),
  acquiredOn: z.string().nullable(),
  acquiredCondition: z.enum(['new', 'used']).nullable(),
  cost: z.number().nullable(),
  currency: z.string().trim().toUpperCase().length(3),
  supplier: z.string().trim().max(64),
  warrantyEndOn: z.string().nullable(),
});
export type AssetFormValues = z.infer<typeof AssetFormSchema>;

export const EMPTY_ASSET_FORM: AssetFormValues = {
  category: '',
  modelKey: null,
  localMode: false,
  localModelName: '',
  localModelMaker: '',
  localSeries: '',
  localPartNumber: '',
  localSpecPowerKw: null,
  localSpecCapacityKva: null,
  localSpecCapacityAh: null,
  localSpecVoltageV: null,
  localSpecWattPeakW: null,
  localSpecCoolingKw: null,
  localSpecTankL: null,
  localSpecBatteryChem: null,
  localSpecBatteryTech: null,
  localWarrantyMonths: null,
  localNetworked: false,
  ratingValue: null,
  ratingUnit: null,
  reference: '',
  referenceAlt: '',
  serial: '',
  name: '',
  unitCount: null,
  condition: null,
  soh: null,
  locationKind: 'site',
  siteId: null,
  status: 'deployed',
  sinceOn: null, // the create wizard pre-fills the PROJECT's today (ADR-082 §6) — never the module's load day
  parentId: null,
  booksOwner: null,
  booksMode: 'purchased',
  acquiredOn: null,
  acquiredCondition: null,
  cost: null,
  currency: '',
  supplier: '',
  warrantyEndOn: null,
};

// ── conveniences ─────────────────────────────────────────────────────────────

const REF_ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789';

/** A usable tag when the client scheme doesn't exist yet (import matches on
 *  reference): `<category prefix>-<4 chars>` — collision-unlikely; a duplicate
 *  409s at save and regenerates. */
export function suggestReference(category: AssetCategory): string {
  const s = Array.from({ length: 4 }, () => REF_ALPHABET[Math.floor(Math.random() * REF_ALPHABET.length)]).join('');
  return `${ASSET_CATEGORY_DESCRIPTORS[category].referencePrefix}-${s}`;
}

/** Whether a reference is still the one `suggestReference` makes for the category (its prefix and four of the
 *  alphabet) — a kind change suggests again; a typed one stays. */
export function isSuggestedReference(reference: string, category: AssetCategory): boolean {
  const prefix = `${ASSET_CATEGORY_DESCRIPTORS[category].referencePrefix}-`;
  return reference.length === prefix.length + 4 && reference.startsWith(prefix) && [...reference.slice(prefix.length)].every((c) => REF_ALPHABET.includes(c));
}

/** The local product draft — the old kind's product, so a kind change clears it whole. */
const LOCAL_DRAFT_KEYS = [
  'localModelName',
  'localModelMaker',
  'localSeries',
  'localPartNumber',
  'localSpecPowerKw',
  'localSpecCapacityKva',
  'localSpecCapacityAh',
  'localSpecVoltageV',
  'localSpecWattPeakW',
  'localSpecCoolingKw',
  'localSpecTankL',
  'localSpecBatteryChem',
  'localSpecBatteryTech',
  'localWarrantyMonths',
  'localNetworked',
] as const satisfies readonly (keyof AssetFormValues)[];

/** The books step's answers — the mode and the currency are its defaults, never sent alone. */
const BOOKS_KEYS = ['booksOwner', 'acquiredOn', 'acquiredCondition', 'cost', 'supplier', 'warrantyEndOn'] as const satisfies readonly (keyof AssetFormValues)[];

const held = (x: unknown): boolean => x !== null && !(typeof x === 'string' && x.trim() === '');

/** What a fact of a kind change names — `connectivity` is the page's own form, cleared and named by the page. */
export type AssetKindClear = 'product' | 'rating' | 'serial' | 'unitCount' | 'parent' | 'soh' | 'books' | 'connectivity';

/** A kind change on the draft — both create wizards alike: what both kinds ask STAYS (a typed reference · the name ·
 *  the condition · the place · the books while the new kind keeps them · a rating in the same unit · the serial while
 *  the new kind is no group · the state of health while it is a battery · the holder while both kinds sit inside the
 *  same kinds); the product is the old kind's, so it goes (the host preselects the new kind's generic); a suggested
 *  reference is suggested again; the rest is CLEARED, and the facts that held a value come back to be said.
 *  `catalogChosen`: the person chose a catalogue product (the preselected generic is not an answer); a local product
 *  draft counts once any of its facts holds a value. */
export function changeAssetKind(v: AssetFormValues, next: AssetCategory, catalogChosen: boolean): { patch: Partial<AssetFormValues>; cleared: AssetKindClear[] } {
  const prev = v.category === '' ? null : (v.category as AssetCategory);
  const was = prev ? ASSET_CATEGORY_DESCRIPTORS[prev] : undefined;
  const now = ASSET_CATEGORY_DESCRIPTORS[next];
  const cleared: AssetKindClear[] = [];
  const patch: Partial<AssetFormValues> = { category: next, modelKey: null, localMode: false, ratingUnit: ratingUnitFor(next) };
  for (const k of LOCAL_DRAFT_KEYS) (patch as Record<string, unknown>)[k] = EMPTY_ASSET_FORM[k];
  const localDrafted = v.localMode && LOCAL_DRAFT_KEYS.some((k) => k !== 'localNetworked' && held(v[k]));
  if (catalogChosen || localDrafted) cleared.push('product');
  if (!v.reference.trim() || (prev !== null && isSuggestedReference(v.reference, prev))) patch.reference = suggestReference(next);
  const unit = ratingUnitFor(next);
  if (unit === null || prev === null || ratingUnitFor(prev) !== unit) {
    if (v.ratingValue !== null) cleared.push('rating');
    patch.ratingValue = null;
  }
  if (now.members && v.serial.trim()) {
    cleared.push('serial');
    patch.serial = '';
  }
  if (!(now.members && was?.members && now.members.unit === was.members.unit)) {
    if (v.unitCount !== null) cleared.push('unitCount');
    patch.unitCount = null;
  }
  if (factState(next, 'soh') === 'na') {
    if (v.soh !== null) cleared.push('soh');
    patch.soh = null;
  }
  const holders = (c?: AssetCategoryCharter) => [...(c?.parentCategories ?? [])].sort().join(',');
  if (holders(now) === '' || holders(was) !== holders(now)) {
    if (v.parentId) cleared.push('parent');
    patch.parentId = null;
  }
  if (!isBooksCategory(next)) {
    if (BOOKS_KEYS.some((k) => held(v[k]))) cleared.push('books');
    for (const k of BOOKS_KEYS) (patch as Record<string, unknown>)[k] = EMPTY_ASSET_FORM[k];
    patch.booksMode = EMPTY_ASSET_FORM.booksMode;
  }
  return { patch, cleared };
}

// The category's usual rating unit (the Generic fast path) now has ONE home:
// `ratingUnitFor` in packages/schemas categories — the same derivation the
// backend's nag predicate and verb validation read.

// ── the model picker's option model (ADR-065 grammar) ────────────────────────

/** The products of the form's category on the family's lookup. */
export interface ProductLookup {
  /** Every product row met on the category's source — by the field (the start answer, the typed answers) and the
   *  row a form seeded — by id: what names the chosen and feeds the derivations (capabilities · SIM slots · warranty ·
   *  the series suggestions). */
  known: ReadonlyMap<string, AssetModelSearchItem>;
  /** The field's source — null without a category. */
  source: ServerSource<'asset-models'> | null;
}

const NO_PRODUCTS: LookupSource<'asset-models'> = { entity: 'asset-models', params: {} };
const NO_IDS: readonly string[] = [];

/** The products of ONE category on the family's lookup (ADR-080 §3 — the catalogue's search, 20 by the typed text,
 *  "Used in this project" first, the category's generic row pinned LAST by the server). The host's hook asks NOTHING
 *  of its own (ADR-080 §7 — the list loads on the press): it reads the rows the FIELD meets on the same source (the
 *  family keeps them once per source) and the row an edit form seeds (`known` — the search names no ids); the wizard's
 *  generic preselect asks the start answer through the field's own cache entry (`lookupSearchOptions`). */
export function useProductLookup(projectId: string, category: AssetCategory | '', opts: { known?: readonly AssetModelSearchItem[] } = {}): ProductLookup {
  const face = useProductFace();
  const { known } = opts;
  const source = useMemo(() => (category ? productSource(projectId, category, face, { known }) : null), [projectId, category, face, known]);
  const look = useLookup(projectId, source?.lookup ?? NO_PRODUCTS, NO_IDS, { enabled: source !== null, open: false, known });
  return { known: look.known, source };
}

/** The row an edit form seeds from the product reference the unit carries — naming only (the facts the family's
 *  derivations read — capabilities · SIM slots · warranty — arrive with the answer's own row, which replaces this one). */
export function productRowOf(ref: AssetModelRef, category: AssetCategory): AssetModelSearchItem {
  return { row_version: 1, // a naming-only seed for the picker, never sent back — the model's real stamp arrives with its own row
    id: ref.id,
    category,
    manufacturer: ref.manufacturer,
    name: ref.label,
    series: null,
    part_number: null,
    description: null,
    is_canonical: ref.is_canonical,
    is_generic: ref.is_generic,
    is_network_device: null,
    capabilities: [],
    interfaces: {},
    power_kw: null,
    capacity_kva: null,
    capacity_ah: null,
    voltage_v: null,
    watt_peak_w: null,
    cooling_kw: null,
    tank_l: null,
    battery_chemistry: null,
    battery_technology: null,
    warranty_months: null,
    is_disabled: ref.is_disabled,
    in_use_count: 0,
    group: null,
  };
}

// ── the vocabularies as sources (ADR-080 §3 — the code kind), built once per language ──

export interface AssetVocabularies {
  condition: CodeSource;
  acquisitionMode: CodeSource;
  acquiredAs: CodeSource;
  chemistry: CodeSource;
  /** Each technology carries its chemistry (`parent`): the field declares `dependsOn` — a chemistry change keeps a
   *  technology that belongs and says what it cleared (ADR-080 §9); the type refuses the field without it. */
  technology: ParentedCodeSource;
}

export function useAssetVocabularies(t: TFunction): AssetVocabularies {
  return useMemo(() => {
    const rows = (ids: readonly string[], word: (id: string) => string, parent?: (id: string) => string): OptionRow[] =>
      ids.map((id) => ({ id, label: word(id), pickable: true, group: null, ...(parent ? { parent: parent(id) } : {}) }));
    return {
      condition: codeSource('asset.condition', rows(['good', 'fair', 'poor', 'failed'], (g) => t(`condition.${g}`))),
      acquisitionMode: codeSource('asset.acquisitionMode', rows(['purchased', 'leased', 'customer_provided', 'other'], (m) => t(`acquisitionMode.${m}`))),
      acquiredAs: codeSource('asset.acquiredAs', rows(['new', 'used'], (w) => t(`acquiredAs.${w}`))),
      chemistry: codeSource('battery.chemistry', rows(BATTERY_CHEMISTRIES, (c) => t(`form.model.batteryChem.${c}`))),
      technology: parentedCodeSource(
        'battery.technology',
        rows(BATTERY_TECHNOLOGIES, (v) => t(`form.model.batteryTechnology.${v}`), (v) => TECHNOLOGY_CHEMISTRY[v as BatteryTechnology]),
      ),
    };
  }, [t]);
}

// ── the shared field context ─────────────────────────────────────────────────

export interface AssetFormCtx {
  t: TFunction;
  projectId: string;
  form: UseFormReturn<AssetFormValues>;
  category: AssetCategory | '';
  descriptor: AssetCategoryCharter | undefined;
  models: ProductLookup;
  /** The forms' vocabularies as the family's code sources (condition · acquisition mode · acquired as · chemistry · technology). */
  vocab: AssetVocabularies;
  /** The attached owners as the family's small source — asked whole on the press. */
  owners: SmallSource;
  /** The currencies as the family's source: the project's own first, then its countries', then all. */
  currency: SmallSource;
  /** asset.models:manage — the local-birth panel renders only for holders. */
  canModels: boolean;
  generic: boolean;
  networkCapable: boolean;
  /** The picked catalog model (undefined when generic / local / none) — the
   *  warranty prefill and the review read it. */
  picked: AssetModelSearchItem | undefined;
  /** Drawer host — grids stack single-column (the architect's narrow ruling). */
  narrow: boolean;
  ratingRef: RefObject<HTMLInputElement | null>;
}

/** The watch-derived half of the context (each container owns its shell). */
export function useAssetFormCtx(opts: {
  t: TFunction;
  projectId: string;
  form: UseFormReturn<AssetFormValues>;
  models: ProductLookup;
  canModels: boolean;
  narrow?: boolean;
}) {
  const { form, projectId } = opts;
  const vocab = useAssetVocabularies(opts.t);
  const owners = useMemo(() => ownersSource(projectId), [projectId]);
  // The project's base currency is the DEFAULT of a new form's currency — set by the HOST at the form's birth (the
  // wizard's defaults; the books drawer's, for a row without books), never written into a living form: a default is
  // not a change, so a form left untouched stays clean, and a field on a later step (the wizard's books step) is born
  // with it. The source leads with it; the held code rides the source too, so an edit form always shows what it holds.
  const { i18n } = useTranslation();
  const projectCurrency = useProject(projectId).data?.default_currency ?? null;
  const heldCurrency = form.formState.defaultValues?.currency || null;
  const currency = useMemo(() => projectCurrencySource(projectId, i18n.language, projectCurrency, heldCurrency), [projectId, i18n.language, projectCurrency, heldCurrency]);
  const category = (form.watch('category') || '') as AssetCategory | '';
  const modelKey = form.watch('modelKey');
  const descriptor = category ? ASSET_CATEGORY_DESCRIPTORS[category] : undefined;
  const row = modelKey ? opts.models.known.get(modelKey) : undefined;
  // The form holds a product's REAL id — the category's generic row is one (the wizard preselects it once the start
  // answer names it; the record drawer seeds the row it holds).
  const generic = row?.is_generic === true;
  const picked = row && !row.is_generic ? row : undefined;
  // The resolved capability: local birth honours the panel's own Networked
  // toggle (the model override it is about to create); a picked product's
  // is_network_device otherwise; the charter default last.
  const localMode = form.watch('localMode');
  const localNetworked = form.watch('localNetworked');
  const networkCapable = category
    ? isNetworkCapable(category, localMode ? localNetworked : (picked?.is_network_device ?? null))
    : false;

  // Focus lands on the rating the moment a category is picked (the fast path is
  // Generic + a size — typing starts immediately). Imperative focus in an
  // effect — it runs after the field commits, deterministically.
  const ratingRef = useRef<HTMLInputElement>(null);
  const [focusRatingPending, setFocusRatingPending] = useState(false);
  useEffect(() => {
    if (focusRatingPending && ratingRef.current) {
      ratingRef.current.focus();
      setFocusRatingPending(false);
    }
  }, [focusRatingPending]);

  const ctx: AssetFormCtx & { requestRatingFocus: () => void } = {
    t: opts.t,
    projectId,
    picked,
    form,
    category,
    descriptor,
    models: opts.models,
    vocab,
    owners,
    currency,
    canModels: opts.canModels,
    generic,
    networkCapable,
    narrow: opts.narrow ?? false,
    ratingRef,
    requestRatingFocus: () => setFocusRatingPending(true),
  };
  return ctx;
}
export type AssetFormCtxWithFocus = ReturnType<typeof useAssetFormCtx>;

// ── the field sections (ONE body, three containers) ──────────────────────────

export function ProductFields({ d }: { d: AssetFormCtx }) {
  const { t, form } = d;
  const localMode = form.watch('localMode');
  // EXCLUSIVE local mode (architect: an open disclosure beside a selected model
  // read as two competing answers): the local-birth panel REPLACES the picker.
  if (localMode) {
    return (
      <div className="space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface-soft)] p-4">
        <div>
          <div className="text-sm font-semibold text-[var(--color-text-primary)]">
            {form.watch('localModelName').trim()
              ? `${t('form.model.localTitle')}: ${form.watch('localModelName').trim()}`
              : t('form.model.localTitle')}
          </div>
          <p className="mt-0.5 text-sm text-[var(--color-text-secondary)]">{t('form.model.notFoundHint')}</p>
        </div>
        <div className={d.narrow ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2'}>
          <div>
            <Label htmlFor="af-lm" required>{t('form.model.localName')}</Label>
            <Input id="af-lm" dir="ltr" {...form.register('localModelName')} />
          </div>
          <div>
            <Label htmlFor="af-lk">{t('form.model.localMaker')}</Label>
            <Input id="af-lk" dir="ltr" {...form.register('localModelMaker')} />
          </div>
          <div>
            <Label htmlFor="af-ls">{t('form.model.localSeries')}</Label>
            {/* suggest-existing (the big-lens grouping): the category search's
                distinct series feed the datalist; free text stays legal */}
            <Input id="af-ls" dir="ltr" list="af-series-options" {...form.register('localSeries')} />
            <datalist id="af-series-options">
              {[...new Set([...d.models.known.values()].map((m) => m.series).filter(Boolean))].map((s) => (
                <option key={s as string} value={s as string} />
              ))}
            </datalist>
          </div>
          <div>
            <Label htmlFor="af-lpn">{t('form.model.localPartNumber')}</Label>
            <Input id="af-lpn" dir="ltr" className="font-mono" {...form.register('localPartNumber')} />
          </div>
        </div>
        <LocalSpecFields d={d} />
        <div>
          <Label htmlFor="af-warranty">{t('form.model.warrantyMonths')}</Label>
          <Controller
            control={form.control}
            name="localWarrantyMonths"
            render={({ field }) => (
              <NumberInput id="af-warranty" value={field.value} onChange={field.onChange} />
            )}
          />
        </div>
        {/* the networked switch is structurally ABSENT on a connectivity-na
            category (ADR-067 rev — the charter says it cannot speak) */}
        {d.category !== '' && !isConnectivityNa(d.category) && (
          <div className="border-t border-[var(--color-border)] pt-3">
            <Toggle
              checked={form.watch('localNetworked')}
              onChange={(on) => form.setValue('localNetworked', on, { shouldDirty: true })}
              label={t('form.model.connectivity.networked')}
            />
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t('form.model.connectivity.laterHint')}</p>
          </div>
        )}
        <Button
          variant="link"
          onClick={() => {
            form.setValue('localMode', false, { shouldDirty: true });
            form.setValue('localModelName', '', { shouldDirty: true });
            form.setValue('localModelMaker', '', { shouldDirty: true });
            resetLocalModelFields(form);
          }}
        >
          {t('form.model.backToCatalog')}
        </Button>
      </div>
    );
  }
  // The local birth's door — the list's last row for `asset.models:manage` holders (ADR-080 §15's shape; ADR-065's
  // findability → create loop): a press opens the panel that REPLACES the picker.
  const openLocalBirth = () => {
    form.setValue('localMode', true, { shouldDirty: true });
    form.setValue('modelKey', null, { shouldDirty: true });
    form.setValue('ratingValue', null, { shouldDirty: true });
    form.setValue('ratingUnit', null, { shouldDirty: true });
    // the "networked device" switch opens pre-set from the charter's three-state — the right fields show without a
    // click on the usual case (an na category shows no switch at all)
    form.setValue('localNetworked', d.descriptor?.connectivity === 'on', { shouldDirty: true });
  };
  return (
    <div className="space-y-4">
      <Controller
        control={form.control}
        name="modelKey"
        render={({ field, fieldState }) =>
          d.models.source ? (
            <Picker
              source={d.models.source}
              label={t('form.model.label')}
              placeholder={t('form.model.placeholder')}
              value={field.value}
              onChange={(v) => {
                field.onChange(v);
                // the rating pair is a generic-only fact (ADR-065 rung 2)
                if (v !== null && d.models.known.get(v)?.is_generic === true) {
                  if (d.category) form.setValue('ratingUnit', ratingUnitFor(d.category), { shouldDirty: true });
                } else {
                  form.setValue('ratingValue', null, { shouldDirty: true });
                  form.setValue('ratingUnit', null, { shouldDirty: true });
                }
              }}
              clearable
              error={fieldState.error?.message}
              tail={d.canModels ? { label: t('form.model.addNew'), icon: <icons.add size={16} aria-hidden />, onAction: openLocalBirth } : undefined}
            />
          ) : (
            <></>
          )
        }
      />
      {/* The rating pair exists only where the category has a headline number
          (ratingUnitFor — ADR-065 rung 2): a generic gateway/cabinet/controller
          asks nothing (eligibility, not emptiness). */}
      {d.generic && d.category && ratingUnitFor(d.category) !== null && (
        <div>
          <Label htmlFor="af-rv">{t('form.model.ratingValue')}</Label>
          {/* The unit is DERIVED, never asked (the charter law): the category
              names it; the form states it as a suffix and the verb enforces
              the match. */}
          <div className="flex items-center gap-2">
            <Controller
              control={form.control}
              name="ratingValue"
              render={({ field }) => (
                <NumberInput id="af-rv" ref={d.ratingRef} value={field.value} onChange={field.onChange} decimals={1} />
              )}
            />
            <span className="shrink-0 text-sm text-[var(--color-text-secondary)]">
              {t(`ratingUnit.${ratingUnitFor(d.category)}`)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function resetLocalModelFields(form: UseFormReturn<AssetFormValues>): void {
  form.setValue('localSeries', '', { shouldDirty: true });
  form.setValue('localPartNumber', '', { shouldDirty: true });
  form.setValue('localSpecPowerKw', null, { shouldDirty: true });
  form.setValue('localSpecCapacityKva', null, { shouldDirty: true });
  form.setValue('localSpecCapacityAh', null, { shouldDirty: true });
  form.setValue('localSpecVoltageV', null, { shouldDirty: true });
  form.setValue('localSpecWattPeakW', null, { shouldDirty: true });
  form.setValue('localSpecCoolingKw', null, { shouldDirty: true });
  form.setValue('localSpecTankL', null, { shouldDirty: true });
  form.setValue('localSpecBatteryChem', null, { shouldDirty: true });
  form.setValue('localSpecBatteryTech', null, { shouldDirty: true });
  form.setValue('localWarrantyMonths', null, { shouldDirty: true });
  form.setValue('localNetworked', false, { shouldDirty: true });
}

// The category's datasheet numbers at birth (the override ladder's rung 3 —
// facts live on the MODEL for a named product; usually ONE field, battery = 3).
const LOCAL_SPEC_FIELD: Record<ModelSpecColumn, { name: keyof AssetFormValues; decimals?: 1 | 2 }> = {
  power_kw: { name: 'localSpecPowerKw', decimals: 2 },
  capacity_kva: { name: 'localSpecCapacityKva', decimals: 2 },
  capacity_ah: { name: 'localSpecCapacityAh' },
  voltage_v: { name: 'localSpecVoltageV', decimals: 1 },
  watt_peak_w: { name: 'localSpecWattPeakW' },
  cooling_kw: { name: 'localSpecCoolingKw', decimals: 2 },
  tank_l: { name: 'localSpecTankL' },
  battery_chemistry: { name: 'localSpecBatteryChem' },
  battery_technology: { name: 'localSpecBatteryTech' },
};

function LocalSpecFields({ d }: { d: AssetFormCtx }) {
  const { t, form } = d;
  const cols = d.descriptor?.modelSpecColumns ?? [];
  if (cols.length === 0) return null;
  return (
    <div className={d.narrow ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2'}>
      {cols.map((col) =>
        col === 'battery_chemistry' ? (
          <Controller
            key={col}
            control={form.control}
            name="localSpecBatteryChem"
            render={({ field, fieldState }) => (
              // classify-at-known-depth: a technology outside the stated chemistry can't stand — the technology field's
              // own parent rule clears it and says so (ADR-080 §9)
              <Picker source={d.vocab.chemistry} label={t('form.model.chemistry')} value={field.value} onChange={(v) => field.onChange(v ?? null)} clearable error={fieldState.error?.message} />
            )}
          />
        ) : col === 'battery_technology' ? (
          <Controller
            key={col}
            control={form.control}
            name="localSpecBatteryTech"
            render={({ field, fieldState }) => {
              const chem = form.watch('localSpecBatteryChem') as BatteryChemistry | null;
              return (
                <Picker
                  source={d.vocab.technology}
                  // under a chemistry only its technologies are offered; a chemistry change keeps what belongs, says the rest
                  dependsOn={{ value: chem, label: chem ? t(`form.model.batteryChem.${chem}`) : undefined, case: 'must_belong' }}
                  label={t('form.model.technology')}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v ?? null);
                    // stating a technology states its chemistry (derivable) — an empty parent becoming a value never clears
                    if (v) form.setValue('localSpecBatteryChem', TECHNOLOGY_CHEMISTRY[v as BatteryTechnology], { shouldDirty: true });
                  }}
                  clearable
                  error={fieldState.error?.message}
                />
              );
            }}
          />
        ) : (
          <div key={col}>
            <Label htmlFor={`af-spec-${col}`}>{t(`form.model.spec.${col}`)}</Label>
            <Controller
              control={form.control}
              name={LOCAL_SPEC_FIELD[col].name as 'localSpecPowerKw'}
              render={({ field }) => (
                <NumberInput
                  id={`af-spec-${col}`}
                  value={field.value}
                  onChange={field.onChange}
                  decimals={LOCAL_SPEC_FIELD[col].decimals}
                />
              )}
            />
          </div>
        ),
      )}
    </div>
  );
}

/** The local-birth POST body, shared by the wizard and the edit drawer (they
 *  must never drift). Identity + specs + the networked override only — the
 *  device half (capabilities · interfaces) is the catalog surface's deliberate
 *  door (`pages/catalog/ProductCreateWizard`), never the inline one. */
export function buildLocalModelBody(v: AssetFormValues): CreateAssetModelRequest {
  const cat = v.category as AssetCategory;
  const networked = v.localNetworked;
  // The category's datasheet numbers — only descriptor-legal keys travel
  // (the server refuses illegal ones; the form only renders legal ones).
  const legal = new Set<ModelSpecColumn>(ASSET_CATEGORY_DESCRIPTORS[cat].modelSpecColumns);
  const specs: Partial<Pick<CreateAssetModelRequest, ModelSpecColumn>> = {};
  const putNum = (col: Exclude<ModelSpecColumn, 'battery_chemistry' | 'battery_technology'>, val: number | null) => {
    if (legal.has(col) && val !== null && val > 0) specs[col] = val;
  };
  putNum('power_kw', v.localSpecPowerKw);
  putNum('capacity_kva', v.localSpecCapacityKva);
  putNum('capacity_ah', v.localSpecCapacityAh);
  putNum('voltage_v', v.localSpecVoltageV);
  putNum('watt_peak_w', v.localSpecWattPeakW);
  putNum('cooling_kw', v.localSpecCoolingKw);
  putNum('tank_l', v.localSpecTankL);
  if (legal.has('battery_chemistry') && v.localSpecBatteryChem) {
    specs.battery_chemistry = v.localSpecBatteryChem as CreateAssetModelRequest['battery_chemistry'];
  }
  if (legal.has('battery_technology') && v.localSpecBatteryTech) {
    specs.battery_technology = v.localSpecBatteryTech as CreateAssetModelRequest['battery_technology'];
  }

  return {
    category: cat,
    name: v.localModelName.trim(),
    manufacturer: v.localModelMaker.trim() || null,
    ...(v.localSeries.trim() ? { series: v.localSeries.trim() } : {}),
    ...(v.localPartNumber.trim() ? { part_number: v.localPartNumber.trim() } : {}),
    ...(v.localWarrantyMonths && v.localWarrantyMonths > 0 ? { warranty_months: v.localWarrantyMonths } : {}),
    ...specs,
    // NULL = inherit: the override is sent only when the switch was flipped
    // away from the charter's three-state default, so the model keeps
    // following the category everywhere else. An `na` category has no switch
    // and never sends one (the verb rejects it anyway — ADR-067 rev).
    ...(!isConnectivityNa(cat) && networked !== (ASSET_CATEGORY_DESCRIPTORS[cat].connectivity === 'on')
      ? { is_network_device: networked }
      : {}),
  };
}

export function IdentityFields({ d, onGenerate }: { d: AssetFormCtx; onGenerate?: () => void }) {
  const { t, form, category } = d;
  if (!category) return null;
  // The member pair (a string of modules / of panels): unit count is the
  // group's identity fact; serial applicability is per-charter (a prepackaged
  // rack's serial is real — the walk's applies-quiet ruling).
  const aggregate = d.descriptor?.members ? { unitLabel: d.descriptor.members.word } : undefined;
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="af-ref" required>{t('peek.fields.reference')}</Label>
        <div className="flex gap-2">
          <Input
            id="af-ref"
            dir="ltr"
            className="flex-1 font-mono"
            {...form.register('reference')}
            error={form.formState.errors.reference ? ' ' : undefined}
          />
          {onGenerate && (
            <Button variant="secondary" onClick={onGenerate}>
              {t('form.fields.generate')}
            </Button>
          )}
        </div>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t('form.fields.referenceHint')}</p>
      </div>
      <div className={d.narrow ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2'}>
        <div>
          <Label htmlFor="af-alt">{t('form.fields.referenceAlt')}</Label>
          <Input id="af-alt" dir="ltr" className="font-mono" {...form.register('referenceAlt')} />
        </div>
        {!aggregate && (
          <div>
            <Label htmlFor="af-sn">{t('list.cols.serial')}</Label>
            <Input id="af-sn" dir="ltr" className="font-mono" {...form.register('serial')} />
          </div>
        )}
      </div>
      <div>
        <Label htmlFor="af-nm">{t('peek.fields.name')}</Label>
        <Input id="af-nm" {...form.register('name')} />
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t('form.fields.nameHint')}</p>
      </div>
      {aggregate && (
        <div className="max-w-48">
          <Label htmlFor="af-uc">{t(`form.fields.unitCount.${aggregate.unitLabel}`)}</Label>
          <Controller
            control={form.control}
            name="unitCount"
            render={({ field }) => <NumberInput id="af-uc" value={field.value} onChange={field.onChange} />}
          />
        </div>
      )}
    </div>
  );
}

export function ConditionNetworkFields({ d }: { d: AssetFormCtx }) {
  const { t, form, category } = d;
  // The GRADE is universal (architect-ruled 4-point; empty = not assessed).
  // SOH stays the battery categories' quantitative EVIDENCE beside it.
  // The address is deliberately NOT here: the wizard's own Connectivity step
  // and the Edit-connectivity drawer are the facet's declared editors.
  const battery = category !== '' && factState(category, 'soh') !== 'na';
  return (
    <div className={d.narrow ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2'}>
      <Controller
        control={form.control}
        name="condition"
        render={({ field, fieldState }) => (
          <Picker source={d.vocab.condition} label={t('filters.condition')} value={field.value} onChange={(v) => field.onChange((v ?? null) as AssetFormValues['condition'])} clearable error={fieldState.error?.message} />
        )}
      />
      {battery && (
        <div>
          <Label htmlFor="af-soh">{t('peek.fields.soh')}</Label>
          <Controller
            control={form.control}
            name="soh"
            render={({ field }) => <NumberInput id="af-soh" value={field.value} onChange={field.onChange} percent />}
          />
        </div>
      )}
    </div>
  );
}

// The DUAL-HOMED pair (architect-ruled): supplier + warranty are the two
// acquisition facts FIELD OPERATIONS generates and consumes (claims, RMA), so
// they render in BOTH edit drawers — the server gates per FIELD, so the two
// forms can never disagree about authority. Deliberately NOT conditional on
// how-acquired: a leased unit is exactly where the lessor gets chased.
export function SupplierWarrantyFields({ d }: { d: AssetFormCtx }) {
  const { t, form } = d;
  const weekStart = useWeekStart(d.projectId);
  const { zone } = useDisplayZone({ kind: 'project', projectId: d.projectId });
  // The day-1 graduated fact (the lifecycle law): a picked product's standard
  // warranty prefills END = acquired + months — editable, never forced; only
  // an empty field is written, so a typed date is never clobbered.
  const acquiredOn = form.watch('acquiredOn');
  const months = d.picked?.warranty_months ?? null;
  useEffect(() => {
    if (!months || !acquiredOn) return;
    if (form.getValues('warrantyEndOn')) return;
    const dt = new Date(`${acquiredOn}T00:00:00Z`);
    if (Number.isNaN(dt.getTime())) return;
    const dayOfMonth = dt.getUTCDate();
    dt.setUTCMonth(dt.getUTCMonth() + months);
    // A month-end anniversary that overflows (Aug 31 + 6 mo → "Feb 31")
    // clamps to the target month's last day instead of spilling into March.
    if (dt.getUTCDate() !== dayOfMonth) dt.setUTCDate(0);
    form.setValue('warrantyEndOn', dt.toISOString().slice(0, 10), { shouldDirty: true });
  }, [months, acquiredOn, form]);
  return (
    <div className={d.narrow ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2'}>
      <div>
        <Label htmlFor="af-sup">{t('peek.fields.supplier')}</Label>
        <Input id="af-sup" {...form.register('supplier')} />
      </div>
      <Controller
        control={form.control}
        name="warrantyEndOn"
        render={({ field }) => <DatePicker label={t('form.fields.warrantyEndOn')} value={field.value} onChange={field.onChange} weekStart={weekStart} zone={zone} />}
      />
    </div>
  );
}

/** The decimals a cost may count — its currency's minor units; two until a currency is known. */
const currencyDecimals = (code: string): 0 | 1 | 2 | 3 | 4 => {
  const n = code && code.length === 3 ? Math.min(4, minorUnitsOf(code)) : 2;
  return n as 0 | 1 | 2 | 3 | 4;
};

export function BooksFields({ d }: { d: AssetFormCtx }) {
  const { t, form } = d;
  const weekStart = useWeekStart(d.projectId);
  const { zone } = useDisplayZone({ kind: 'project', projectId: d.projectId });
  const two = d.narrow ? 'grid gap-4' : 'grid gap-4 sm:grid-cols-2';
  const currencyCode = form.watch('currency');
  return (
    <div className="space-y-4">
      <div className={two}>
        {/* The owner — the attached owners as a small list asked whole on the press; a disabled owner takes no new
            equipment (hidden at rest), an edit keeps showing the saved one, marked (ADR-080 §5). */}
        <Controller
          control={form.control}
          name="booksOwner"
          render={({ field, fieldState }) => (
            <Picker source={d.owners} label={t('list.cols.owner')} value={field.value} onChange={field.onChange} clearable error={fieldState.error?.message} />
          )}
        />
        {/* A field, not a SegmentedControl (architect: mismatched heights beside
            Owner) — inside a form grid, fields speak the field-frame grammar. */}
        <Controller
          control={form.control}
          name="booksMode"
          render={({ field, fieldState }) => (
            <Picker
              source={d.vocab.acquisitionMode}
              label={t('form.fields.mode')}
              value={field.value}
              onChange={(m) => field.onChange((m ?? 'purchased') as AssetFormValues['booksMode'])}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
      <div className={two}>
        <Controller
          control={form.control}
          name="acquiredOn"
          render={({ field }) => <DatePicker label={t('form.fields.acquiredOn')} value={field.value} onChange={field.onChange} weekStart={weekStart} zone={zone} />}
        />
        {/* "New or used", not "condition" — that word belongs to the health grade. A two-row picker: it changes nothing
            that follows (ADR-080 §2 — a row would say otherwise). */}
        <Controller
          control={form.control}
          name="acquiredCondition"
          render={({ field, fieldState }) => (
            <Picker
              source={d.vocab.acquiredAs}
              label={t('form.fields.acquiredCondition')}
              value={field.value}
              onChange={(v) => field.onChange((v ?? null) as AssetFormValues['acquiredCondition'])}
              clearable
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
      {/* The cost and its currency for EVERY mode — a leased or customer-provided unit may carry one; only a purchased
          unit is nagged for a missing one (the register's law, shared with the strip). */}
      <div className={two}>
          <div>
            <Label htmlFor="af-cost">{t('list.cols.cost')}</Label>
            {/* the amount counts its currency's minor units (SAR 2 · LBP 0 · BHD 3) — the write refuses more */}
            <Controller
              control={form.control}
              name="cost"
              render={({ field }) => <NumberInput id="af-cost" value={field.value} onChange={field.onChange} decimals={currencyDecimals(currencyCode)} />}
            />
          </div>
          <Controller
            control={form.control}
            name="currency"
            render={({ field, fieldState }) => (
              <Picker source={d.currency} label={t('form.fields.currency')} value={field.value || null} onChange={(cur) => field.onChange(cur ?? '')} error={fieldState.error?.message} />
            )}
          />
      </div>
      <SupplierWarrantyFields d={d} />
    </div>
  );
}
